import { TimepointSubmissionStatus } from '@common/config/api/client'
import {
  Alert,
  AlertTitle,
  Chip,
  Collapse,
  IconButton,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { East, KeyboardArrowDownRounded, KeyboardArrowUpRounded } from '@mui/icons-material'
import ReassignExamTaskQCTable from './ReassignExamTaskQCTable'
import ReassignExamTaskReadTable from './ReassignExamTaskReadTable'
import { Fragment, useState } from 'react'
import { useParams } from 'react-router-dom'
import { useTimepointExamReassignemenPreview } from '@features/qc_task_details_view/hooks/useTimepointExamReassignemenPreview'
import ReassignExamTimepointTable from './ReassignExamTimepointTable'

export default function ExpandableUpdateSubmissionStatusTasks({
  timepointSubmissionId,
  selectedStatus,
  setSelectedStatus,
  subjectId,
  isCurrent,
  isExpandable = true,
}: {
  timepointSubmissionId?: string
  selectedStatus: TimepointSubmissionStatus | ''
  setSelectedStatus: (status: TimepointSubmissionStatus | '') => void
  subjectId?: string
  isCurrent?: boolean
  isExpandable?: boolean
}) {
  const [isOpen, setIsOpen] = useState(true)
  const toggleOpen = () => setIsOpen((prev) => !prev)

  const { studyId } = useParams<{
    studyId: string
  }>()

  const { data, error, isLoading } = useTimepointExamReassignemenPreview(
    ['TimepointExamReassignemenPreview'],
    studyId!,
    subjectId!,
    timepointSubmissionId!,
    selectedStatus || undefined,
  )

  const handleStatusChange = (event: { target: { value: string } }) => {
    setSelectedStatus(event.target.value as TimepointSubmissionStatus)
  }

  const formatStatus = (status?: string) => {
    if (!status) {
      return '—'
    }
    return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase().replace(/_/g, ' ')
  }

  const availableStatuses: TimepointSubmissionStatus[] = ['IN_PROGRESS', 'COMPLETE', 'ACTIVE', 'INACTIVE']

  if (isLoading || !timepointSubmissionId) {
    return <div>Loading...</div>
  }
  if (error || !data || !data.data) {
    return <div>Error: {error?.message}</div>
  }

  return (
    <Paper
      sx={{
        borderRadius: 3,
        boxShadow: 1,
      }}
    >
      <Stack padding={4} spacing={4}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography color="text.primary" variant="h6">
            {`${data.data.timepoint_submission_name} status`}
          </Typography>
          <Stack direction="row" spacing={1} alignItems="center">
            <Chip label={`${isCurrent ? 'Current' : 'New'} timepoint`} size="small" />
            <Chip label={`Subject ID ${data.data.subject.subject_pseudo_id}`} size="small" />
            {isExpandable && (
              <IconButton onClick={toggleOpen}>
                {isOpen ? <KeyboardArrowUpRounded /> : <KeyboardArrowDownRounded />}
              </IconButton>
            )}
          </Stack>
        </Stack>
        <Collapse in={isExpandable ? isOpen : true} timeout="auto" unmountOnExit>
          <Stack spacing={4}>
            <Stack spacing={3}>
              {selectedStatus === 'INACTIVE' && (
                <Alert
                  severity="warning"
                  variant="standard"
                  sx={{
                    borderRadius: 2,
                  }}
                >
                  <AlertTitle variant="button" color="warning-alt.contrastText">
                    This timepoint will be inactive and hidden from site users
                  </AlertTitle>
                  <Typography color="warning-alt.contrastText" variant="body2">
                    Site users won't see this in the subject list, but it will remain visible in your list.
                  </Typography>
                </Alert>
              )}
              {isExpandable ? (
                <Fragment>
                  <Typography color="text.primary" variant="subtitle1">
                    Submission status
                  </Typography>
                  <Stack direction="row" alignItems="center">
                    <TextField
                      fullWidth
                      value={formatStatus(data?.data?.timepoint_submission_status)}
                      disabled
                      label="Current status"
                      size="small"
                    />
                    <Stack padding={1}>
                      <East />
                    </Stack>
                    <Select
                      fullWidth
                      value={selectedStatus}
                      size="small"
                      label="New status"
                      displayEmpty
                      onChange={handleStatusChange}
                      error={!selectedStatus && isExpandable}
                    >
                      <MenuItem value="" disabled>
                        <em>Select a status</em>
                      </MenuItem>
                      {availableStatuses.map((status) => (
                        <MenuItem key={status} value={status}>
                          {formatStatus(status)}
                        </MenuItem>
                      ))}
                    </Select>
                  </Stack>
                </Fragment>
              ) : (
                <ReassignExamTimepointTable timepoint={data?.data} timepointStatus={selectedStatus} />
              )}
            </Stack>
            {data.data.qc_tasks.length > 0 && <ReassignExamTaskQCTable tasks={data.data.qc_tasks} />}
            {data.data.read_tasks.length > 0 && <ReassignExamTaskReadTable tasks={data.data.read_tasks} />}
          </Stack>
        </Collapse>
      </Stack>
    </Paper>
  )
}
